import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { updateMe } from "@/lib/api";
import { ShoppingBag, Briefcase, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useEffect } from "react";

const ROLES = [
  {
    id: "buyer",
    icon: ShoppingBag,
    title: "I'm here to buy",
    tagline: "Hire craft. Ship faster.",
    bullets: ["Browse thousands of gigs", "Chat directly with sellers", "Track orders from a single inbox"],
  },
  {
    id: "seller",
    icon: Briefcase,
    title: "I'm here to sell",
    tagline: "Post your craft. Own your rate.",
    bullets: ["Publish gigs with 3 tier packages", "Manage orders + deliverables", "Withdraw earnings anytime"],
  },
  {
    id: "both",
    icon: Sparkles,
    title: "Both, actually",
    tagline: "Marketplace on both sides.",
    bullets: ["Buy when you need craft", "Sell when you have craft", "Switch modes anytime"],
  },
];

export default function OnboardingPage() {
  const { user, setUser, loading } = useAuth();
  const navigate = useNavigate();
  const [choice, setChoice] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  const submit = async () => {
    if (!choice) { toast.error("Pick one to continue"); return; }
    setSaving(true);
    try {
      const fresh = await updateMe({ role: choice });
      setUser(fresh);
      toast.success("Welcome to Noxi");
      navigate(choice === "buyer" ? "/buyer/home" : "/dashboard", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  if (!user) return null;

  return (
    <div className="min-h-[85vh] flex items-center justify-center px-6 py-12">
      <div className="max-w-5xl w-full">
        <div className="text-center mb-12">
          <div className="overline text-primary mb-3">◆ WELCOME, {user.name?.split(" ")[0]?.toUpperCase()}</div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-3">How will you use Noxi?</h1>
          <p className="text-muted-foreground max-w-xl mx-auto">Pick a starting side — you can always switch later from your profile.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-10">
          {ROLES.map((r) => {
            const Icon = r.icon;
            const active = choice === r.id;
            return (
              <button
                key={r.id}
                data-testid={`role-${r.id}`}
                onClick={() => setChoice(r.id)}
                className={`text-left p-8 border-2 transition-all ${active ? "border-foreground bg-foreground text-background brutal-shadow" : "border-foreground/15 bg-card hover:border-foreground"}`}
              >
                <Icon className={`w-10 h-10 mb-8 ${active ? "text-primary" : ""}`} strokeWidth={1.5}/>
                <div className={`overline mb-2 ${active ? "text-primary" : "text-muted-foreground"}`}>◆ OPTION {r.id === "buyer" ? "01" : r.id === "seller" ? "02" : "03"}</div>
                <div className="text-2xl font-bold display mb-2">{r.title}</div>
                <div className={`text-sm mb-5 ${active ? "text-background/70" : "text-muted-foreground"}`}>{r.tagline}</div>
                <ul className="space-y-2 text-sm">
                  {r.bullets.map((b) => (
                    <li key={b} className={`flex items-start gap-2 ${active ? "" : "text-foreground/80"}`}>
                      <span className={active ? "text-primary" : "text-primary"}>▸</span>
                      {b}
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        <div className="flex justify-center">
          <button
            data-testid="onboarding-continue-btn"
            onClick={submit}
            disabled={!choice || saving}
            className="bg-primary text-primary-foreground px-10 py-4 mono text-sm font-medium hover:bg-primary/90 disabled:opacity-40 flex items-center gap-2"
          >
            {saving ? "SAVING…" : "CONTINUE"} <ArrowRight className="w-4 h-4"/>
          </button>
        </div>
      </div>
    </div>
  );
}
